<template>
  <b-container v-if="order">
    <b-row v-if="canAccessRefunds" class="mb-3">
      <b-col class="text-right">
        <b-button
          variant="primary"
          :disabled="isFullyRefunded"
          @click="openRefundModal"
        >
          Issue Refund
        </b-button>
      </b-col>
    </b-row>
    <b-row no-gutters class="mb-3 overflow-auto">
      <table class="totals-table">
        <thead>
          <tr>
            <th>Customer Totals</th>
            <th width="15%" class="pr-1">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Subtotal</td>
            <td>
              {{ (order.subtotal / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Discount</td>
            <td>
              {{ (order.discount_total / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Delivery Fee</td>
            <td>
              {{ (order.delivery_fee / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Tip</td>
            <td>
              {{ (order.tip_total / 100) | currency }}
            </td>
          </tr>
          <tr v-if="order.service_fee_total">
            <td>Service Fee</td>
            <td>
              {{ (order.service_fee_total / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Tax</td>
            <td>
              {{ (order.tax_total / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Per Diem</td>
            <td>
              {{ (order.per_diem_total / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Per Diem Tax</td>
            <td>
              {{ (order.per_diem_tax_total / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>
              Order Total
              <span class="icon ml-2">
                <img
                  id="orders-customer-net-totals"
                  :src="asset('/imgs/icons/info.svg')"
                  alt="hours info"
                  class="mb-1"
                />
              </span>
            </td>
            <b-tooltip target="orders-customer-net-totals" triggers="hover">
              This is the original total paid by the customer. If this order was
              refunded, see Refunds table below to see the final post-refund
              cost.
            </b-tooltip>
            <td>
              {{ (order.order_total / 100) | currency }}
            </td>
          </tr>
        </tbody>
      </table>
    </b-row>
    <b-row no-gutters class="mb-3 overflow-auto">
      <table class="first-left-table">
        <thead>
          <tr>
            <th>Kitchen Totals</th>
            <th
              v-for="header in kitchenHeaders"
              :key="header.id"
              class="pr-sm-1"
            >
              {{ header.title }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="row in kitchenTotals" :key="row.title">
            <td>
              {{ row.title }}
              <span v-if="row.title === 'Net Total'" class="icon ml-2">
                <img
                  id="orders-kitchen-net-totals"
                  :src="asset('/imgs/icons/info.svg')"
                  alt="hours info"
                  class="mb-1"
                />
              </span>
            </td>
            <td v-for="(centerVal, center) in row.centers" :key="center.id">
              {{ (centerVal / 100) | currency }}
            </td>
            <td>
              <template v-if="row.amount === false">---</template>
              <template v-else>{{ (row.amount / 100) | currency }}</template>
            </td>
          </tr>
          <b-tooltip target="orders-kitchen-net-totals" triggers="hover">
            This is the original total paid to the kitchen. If this order was
            refunded, see Refunds table below to see the final post-refund
            payment.
          </b-tooltip>
        </tbody>
      </table>
    </b-row>
    <b-row no-gutters class="mb-3 overflow-auto">
      <table>
        <thead>
          <tr>
            <th>Transaction Type</th>
            <th>Status</th>
            <th>Amount</th>
            <th>Method</th>
            <th>ID</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="order.payment">
            <td>Payment</td>
            <td>{{ order.payment.status | capitalize }}</td>
            <td>
              {{ (order.payment.amount / 100) | currency }}
            </td>
            <td>{{ order.payment.type | capitalize }}</td>
            <td>{{ order.payment.id }}</td>
            <td />
          </tr>
          <tr
            v-for="(refund, index) in order.network_order_refunds"
            :key="refund.id"
          >
            <td>Refund #{{ index + 1 }}</td>
            <td>{{ refund.status | capitalize }}</td>
            <td>-{{ (refund.amount / 100) | currency }}</td>
            <td>{{ refund.type | capitalize }}</td>
            <td>{{ refund.id }}</td>
            <td>{{ refund.notes }}</td>
          </tr>
        </tbody>
      </table>
    </b-row>
    <b-row
      v-if="order.network_order_refunds.length"
      no-gutters
      class="mb-3 overflow-auto"
    >
      <table class="first-left-table">
        <thead>
          <tr>
            <th>Refund Totals</th>
            <th>Initial Payment</th>
            <th
              v-for="(refund, index) in order.network_order_refunds"
              :key="refund.id"
            >
              Refund #{{ index + 1 }}
            </th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in refundRows" :key="row.title">
            <td>{{ row.title }}</td>
            <td>{{ (row.initial / 100) | currency }}</td>
            <td v-for="refund in row.refunds" :key="refund.id">
              -{{ (refund / 100) | currency }}
            </td>
            <td>{{ (row.total / 100) | currency }}</td>
          </tr>
        </tbody>
      </table>
    </b-row>
    <b-row
      v-if="order.order_coupons.length"
      no-gutters
      class="mb-3 overflow-auto"
    >
      <table>
        <thead>
          <tr>
            <th>Coupon Code</th>
            <th>Type</th>
            <th>Amount</th>
            <th>Target</th>
            <th>Discount</th>
            <th>Discount Tax</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="coupon in order.order_coupons" :key="coupon.id">
            <td>{{ coupon.code }}</td>
            <td>{{ coupon.type | capitalize }}</td>
            <td>
              <template v-if="coupon.type === 'percent'">
                {{ coupon.amount }}% off
              </template>
              <template v-else>
                {{ (coupon.amount / 100) | currency }} off
              </template>
            </td>
            <td>{{ coupon.target | capitalize }}</td>
            <td>{{ (coupon.discount / 100) | currency }}</td>
            <td>{{ (coupon.discount_tax / 100) | currency }}</td>
          </tr>
        </tbody>
      </table>
    </b-row>
    <refund-modal
      v-if="canAccessRefunds"
      :network-order-id="order.id"
      :show="showRefundModal"
      @dismissed="handleRefundDismissed"
    />
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import RefundModal from '../../components/RefundModal.vue';

export default {
  components: {
    RefundModal,
  },
  props: {
    order: {
      type: Object,
      default() {
        return { id: null };
      },
    },
  },
  data() {
    return {
      showRefundModal: false,
      kitchenFields: [
        {
          title: 'Subtotal',
          kitchenKey: 'subtotal',
          amountKey: 'subtotal',
        },
        {
          title: 'Discount',
          kitchenKey: 'discount_total',
          amountKey: 'discount_total',
        },
        {
          title: 'Reimbursable Discounts',
          kitchenKey: 'reimbursable_discount_total',
          amountKey: false,
        },
        {
          title: 'Tax',
          kitchenKey: 'tax_total',
          amountKey: 'tax_total',
        },
        {
          title: 'Discount Tax',
          kitchenKey: 'discount_tax_total',
          amountKey: 'discount_tax_total',
        },
        {
          title: 'Delivery Fee',
          kitchenKey: 'delivery_charge',
          amountKey: 'delivery_fee',
        },
        {
          title: 'Tip',
          kitchenKey: 'tip_total',
          amountKey: 'tip_total',
        },
        {
          title: 'Zuul Fee',
          kitchenKey: 'zuul_commission',
          amountKey: 'zuul_commission',
        },
        {
          title: 'Facility Fee',
          kitchenKey: 'facility_commission',
          amountKey: 'facility_commission',
        },
        {
          title: 'Partner Fee',
          kitchenKey: 'partner_commission',
          amountKey: 'partner_commission',
        },
        {
          title: 'Employer Fee',
          kitchenKey: 'employer_commission',
          amountKey: 'employer_commission',
        },
        {
          title: 'CC Processing Fee',
          kitchenKey: 'processing_fee',
          amountKey: 'processing_fee',
        },
        {
          title: 'Total Kitchen Fees',
          kitchenKey: 'fee_total',
          amountKey: 'fee_total',
        },
        {
          title: 'Net Total',
          kitchenKey: 'net_total',
          amountKey: false,
        },
      ],
      refundFields: [],
    };
  },
  computed: {
    ...mapGetters('session', ['canRefundOrders', 'isAdmin']),
    kitchenHeaders() {
      const headers = [];

      // Kitchen values
      this.order.fulfillment_center_orders.forEach((el) => {
        headers.push({
          id: el.id,
          title: el.fulfillment_center.name,
        });
      });

      // Total
      headers.push({
        id: -1,
        title: 'Amount',
      });

      return headers;
    },
    kitchenTotals() {
      const data = [];

      this.kitchenFields.forEach((field) => {
        const row = {
          title: field.title,
          centers: {},
          amount: 0,
        };

        // Set kitchen values
        this.order.fulfillment_center_orders.forEach((kitchenOrder) => {
          row.centers[kitchenOrder.id] = kitchenOrder[field.kitchenKey];
        });

        // Set row amount
        if (field.amountKey) {
          row.amount = this.order[field.amountKey];
        } else {
          row.amount = false;
        }

        data.push(row);
      });

      return data;
    },
    refundRows() {
      const data = [];

      // Get unique fulfillment centers involved
      const centerIds = this.order.fulfillment_center_orders.map((i) => {
        return i.fulfillment_center_id;
      });

      // Customer row
      let row = {
        title: 'Customer',
        initial: this.order.order_total,
        total: this.totalSum(
          this.order.network_order_refunds,
          'amount',
          this.order.order_total
        ),
        refunds: {},
      };

      // Calculate the refund amount for each kitchen per refund
      this.order.network_order_refunds.forEach((refund) => {
        row.refunds[refund.id] = refund.amount;
      });

      data.push(row);

      // Kitchen rows
      centerIds.forEach((centerId) => {
        const centerOrder = this.order.fulfillment_center_orders.find(
          (el) => el.fulfillment_center_id === centerId
        );

        let refundTotal = 0;

        row = {
          title: centerOrder.fulfillment_center.name,
          initial: centerOrder.net_total,
          refunds: [],
        };

        // Calculate the refund amount for each kitchen per refund
        this.order.network_order_refunds.forEach((refund) => {
          const centerRefund = refund.fulfillment_center_order_refunds.find(
            (el) => el.fulfillment_center_id === centerId
          );

          row.refunds.push(centerRefund ? centerRefund.net_total : 0);
          refundTotal += centerRefund ? centerRefund.net_total : 0;
        });

        // Set total
        row.total = centerOrder.net_total - refundTotal;

        data.push(row);
      });

      // Partner rows
      const partnerFields = [
        {
          title: 'Zuul',
          key: 'zuul_commission',
        },
        {
          title: 'Facility',
          key: 'facility_commission',
        },
        {
          title: 'Partner',
          key: 'partner_commission',
        },
        {
          title: 'Employer',
          key: 'employer_commission',
        },
      ];

      partnerFields.forEach((field) => {
        row = {
          title: field.title,
          initial: this.order[field.key],
          refunds: [],
          total: 0,
        };

        let partnerRefunds = 0;

        this.order.network_order_refunds.forEach((refund) => {
          row.refunds.push(refund[field.key]);
          partnerRefunds += refund[field.key];
        });

        row.total = row.initial - partnerRefunds;

        data.push(row);
      });

      return data;
    },
    totalRefunded() {
      if (
        !this.order ||
        !this.order.network_order_refunds ||
        !this.order.network_order_refunds.length
      ) {
        return 0;
      }

      return this.order.network_order_refunds.reduce((sum, refund) => {
        return sum + (refund.amount || 0);
      }, 0);
    },
    isFullyRefunded() {
      if (!this.order || !this.order.order_total) {
        return false;
      }

      return this.totalRefunded >= this.order.order_total;
    },
    canAccessRefunds() {
      return this.canRefundOrders || this.isAdmin;
    },
  },
  methods: {
    // Get the net total of a given property
    totalSum(refunds, entity, total) {
      let refundsSum = 0;
      refunds.forEach((element) => {
        refundsSum += element[entity];
      });
      return total - refundsSum;
    },
    openRefundModal() {
      this.showRefundModal = true;
    },
    handleRefundDismissed(issued) {
      this.showRefundModal = false;

      if (issued) {
        this.$emit('refund-issued');
      }
    },
  },
};
</script>
